/**
 * QR Scanner Component
 * Handles camera access, video stream rendering, and QR code decoding.
 *
 * Uses the browser MediaDevices API with rear-facing camera preference
 * and jsQR library for frame-by-frame QR code detection.
 *
 * @example
 *   const scanner = new QrScanner(containerEl, onScan, onError);
 *   await scanner.start();
 *   // ... later
 *   scanner.stop();
 */

import jsQR from '../lib/jsQR.js';

/**
 * Error type constants for camera/scanner failures.
 * @typedef {'permission_denied' | 'no_camera' | 'unsupported' | 'unknown'} QrScannerErrorType
 */

export class QrScanner {
    /**
     * @param {HTMLElement} containerElement - DOM element to render video feed into
     * @param {function(string): void} onScan - Callback invoked with decoded QR text
     * @param {function(QrScannerErrorType): void} onError - Callback invoked on camera/scanner error
     */
    constructor(containerElement, onScan, onError) {
        this.container = containerElement;
        this.onScan = onScan;
        this.onError = onError;
        this.videoElement = null;
        this.canvasElement = null;
        this.canvasContext = null;
        this.stream = null;
        this.animationFrame = null;
        this.scanning = false;
    }

    /**
     * Requests camera access and starts scanning for QR codes.
     * Uses rear-facing camera by default (facingMode: "environment").
     * Calls onError callback if camera access fails.
     */
    async start() {
        // Check if MediaDevices API is supported
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            this.onError('unsupported');
            return;
        }

        const constraints = {
            video: { facingMode: 'environment' }
        };

        try {
            this.stream = await navigator.mediaDevices.getUserMedia(constraints);
            this._renderVideoFeed();
            this._startScanning();
        } catch (error) {
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                this.onError('permission_denied');
            } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
                this.onError('no_camera');
            } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
                this.onError('no_camera');
            } else {
                this.onError('unknown');
            }
        }
    }

    /**
     * Stops scanning and releases all camera media tracks.
     * Cancels the animation frame loop and clears the video feed.
     */
    stop() {
        this.scanning = false;

        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
            this.animationFrame = null;
        }

        if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop());
            this.stream = null;
        }

        if (this.videoElement) {
            this.videoElement.srcObject = null;
        }
    }

    /**
     * Creates and renders the video element and hidden canvas into the container.
     * @private
     */
    _renderVideoFeed() {
        // Create video element for camera stream display
        this.videoElement = document.createElement('video');
        this.videoElement.className = 'qr-scanner__video';
        this.videoElement.setAttribute('autoplay', '');
        this.videoElement.setAttribute('playsinline', '');
        this.videoElement.setAttribute('muted', '');
        this.videoElement.srcObject = this.stream;

        // Create hidden canvas for frame capture and QR decoding
        this.canvasElement = document.createElement('canvas');
        this.canvasElement.className = 'qr-scanner__canvas';
        this.canvasElement.style.display = 'none';
        this.canvasContext = this.canvasElement.getContext('2d', { willReadFrequently: true });

        // Clear container and append elements
        this.container.innerHTML = '';
        this.container.appendChild(this.videoElement);
        this.container.appendChild(this.canvasElement);

        this.videoElement.play();
    }

    /**
     * Begins the requestAnimationFrame loop to capture and decode video frames.
     * @private
     */
    _startScanning() {
        this.scanning = true;
        this._tick();
    }

    /**
     * Single frame capture and decode cycle.
     * Draws current video frame to canvas, runs jsQR, and schedules next frame.
     * @private
     */
    _tick() {
        if (!this.scanning) {
            return;
        }

        if (this.videoElement && this.videoElement.readyState === this.videoElement.HAVE_ENOUGH_DATA) {
            const width = this.videoElement.videoWidth;
            const height = this.videoElement.videoHeight;

            if (width > 0 && height > 0) {
                this.canvasElement.width = width;
                this.canvasElement.height = height;
                this.canvasContext.drawImage(this.videoElement, 0, 0, width, height);

                const imageData = this.canvasContext.getImageData(0, 0, width, height);
                const code = jsQR(imageData.data, width, height);

                if (code && code.data) {
                    // QR code detected — stop camera and notify
                    this.stop();
                    this.onScan(code.data);
                    return;
                }
            }
        }

        this.animationFrame = requestAnimationFrame(() => this._tick());
    }
}
